import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Empty,
  Input,
  List,
  message,
  Modal,
  Skeleton,
  Space,
  Tag,
  Typography,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CreditCardOutlined,
  LinkOutlined,
  ReloadOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import paths from "routes/paths";
import { fetchWorkspace } from "services/workspaceService";
import identitySubscriptionService from "services/subscriptions";
import partnersService from "services/partnersService";
import {
  formatDate,
  planLabel,
  readable,
  subscriptionLabel,
} from "./workspaceUtils";
import { OLIVEEB_COLORS, workspaceButtonStyles } from "./colors";

const { Text } = Typography;

const pageContent = (result) => (Array.isArray(result) ? result : result?.content || []);

const money = (value, currency = "USD") => {
  if (value === undefined || value === null || value === "") return "-";
  const number = Number(value);
  if (Number.isNaN(number)) return String(value);

  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
  }).format(number);
};

const moneyNumber = (value) => {
  if (typeof value === "number") return value;
  if (typeof value === "string") return Number(value.replace(/[^0-9.-]/g, ""));
  return Number(value || 0);
};

const firstString = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number") return String(value);
  }
  return "";
};

const normalizeBgpCode = (value = "") =>
  String(value || "").replace(/[^a-zA-Z0-9]/g, "").trim().toUpperCase();

const normalizeCouponCodes = (value = "") =>
  Array.from(
    new Set(
      String(value || "")
        .split(/[\s,;]+/)
        .map((item) => item.replace(/[^a-zA-Z0-9_-]/g, "").trim().toUpperCase())
        .filter(Boolean),
    ),
  );

const couponCodesText = (codes = []) => {
  if (Array.isArray(codes)) return codes.filter(Boolean).join(", ");
  return normalizeCouponCodes(codes).join(", ");
};

const sectionTitle = (title) => (
  <Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>
    {title}
  </Text>
);

const planRank = (plan) => {
  const value = `${plan?.code || ""} ${plan?.name || ""}`.toUpperCase();
  if (value.includes("FREE")) return 0;
  if (value.includes("STANDARD")) return 1;
  if (value.includes("PREMIUM")) return 2;
  return 3;
};

const sortPlans = (items = []) =>
  [...items].sort((left, right) => {
    const rankDiff = planRank(left) - planRank(right);
    if (rankDiff) return rankDiff;
    return planLabel(left).localeCompare(planLabel(right));
  });

const isFreePlanCode = (planCode) => String(planCode || "").toUpperCase().includes("FREE");
const isFreePlan = (plan) => isFreePlanCode(firstString(plan?.code));
const isPersonalAccount = (account) =>
  String(account?.accountType || "").toUpperCase() === "PERSONAL";
const planKey = (plan) => firstString(plan?.id, plan?.code);
const subscriptionContext = (account, actorId) => {
  if (isPersonalAccount(account)) {
    return {
      planType: "PERSONAL",
      subscriberType: "USER",
      subscriberId: actorId,
      organizationId: "",
      ready: Boolean(actorId),
    };
  }

  return {
    planType: "BUSINESS",
    subscriberType: "BUSINESS",
    subscriberId: account?.organizationId || "",
    organizationId: account?.organizationId || "",
    ready: Boolean(account?.organizationId),
  };
};

const planMonthlyPrice = (plan) => money(plan?.monthlyAmount) || "$0.00";
const planAnnualPrice = (plan) => money(plan?.annualAmount) || "$0.00";
const planAnnualizedMonthlyPrice = (plan) => money(moneyNumber(plan?.monthlyAmount) * 12) || "$0.00";
const planAnnualSavings = (plan) => {
  const savings = (moneyNumber(plan?.monthlyAmount) * 12) - moneyNumber(plan?.annualAmount);
  return savings > 0 ? money(savings) : "";
};

const personalPlanBenefits = (planCode) => {
  const code = String(planCode || "").toUpperCase();
  if (code.includes("FREE")) {
    return [
      "Create a professional profile",
      "Search and apply for jobs",
      "Limited profile visibility",
      "Basic application tracking",
      "No access to verification reports",
    ];
  }
  if (code.includes("STANDARD")) {
    return [
      "Enhanced professional profile",
      "Unlimited job applications",
      "Increased visibility to employers",
      "Application and interview tracking",
      "Access to verification results",
      "Request Olreadia-managed background checks",
      "Direct messaging with employers",
    ];
  }
  if (code.includes("PREMIUM")) {
    return [
      "Advanced professional profile",
      "Priority visibility to employers",
      "AI-powered job recommendations",
      "Resume and profile optimization",
      "Interview preparation and mock interviews",
      "Integrated messaging and email inbox",
      "Access to all verification and background check results",
      "Request Olreadia-managed background checks",
      "Share a verified profile using a QR code",
      "Personal professional website",
    ];
  }
  return [];
};

const businessPlanBenefits = (planCode) => {
  const code = String(planCode || "").toUpperCase();
  if (code.includes("FREE")) {
    return [
      "Basic hiring process management",
      "1 job post per month",
      "1 workspace member",
      "No access to background check results",
    ];
  }
  if (code.includes("STANDARD")) {
    return [
      "Complete hiring process management",
      "Up to 10 workspace members",
      "Up to 5 job posts per month",
      "Access to background check results",
      "Request Olreadia-managed background checks",
    ];
  }
  if (code.includes("PREMIUM")) {
    return [
      "Advanced hiring process management",
      "Unlimited workspace members",
      "Free business website",
      "Integrated messaging and email inbox",
      "Access to background check results",
      "Request Olreadia-managed background checks",
      "Conduct and manage background checks using self-service tools",
    ];
  }
  return [];
};

const planServiceItems = (plan, planType = "BUSINESS") => {
  const rawItems =
    plan?.items ||
    plan?.subscriptionPlanItems ||
    plan?.packageItems ||
    plan?.packageServices ||
    plan?.serviceItems ||
    plan?.offeredItems ||
    plan?.offeredServices ||
    plan?.services ||
    plan?.features ||
    plan?.offerings ||
    plan?.includedItems ||
    [];
  const contentItems = rawItems?.content || rawItems?.items;
  const items = Array.isArray(rawItems)
    ? rawItems
    : Array.isArray(contentItems)
      ? contentItems
      : String(rawItems || "").split(/[\n;]+/);

  const apiItems = items
    .map((item, index) => {
      if (typeof item === "string") {
        return {
          key: `${index}-${item}`,
          label: item.trim(),
          description: "",
        };
      }

      const label = firstString(
        item?.name,
        item?.label,
        item?.title,
        item?.serviceName,
        item?.itemName,
        item?.packageItemName,
        item?.packageServiceName,
        item?.service?.name,
        item?.code && readable(item.code, item.code),
      );

      return {
        key: firstString(item?.id, item?.serviceId, item?.code, item?.name, index),
        label,
        description: firstString(item?.description, item?.summary, item?.details, item?.service?.description),
      };
    })
    .filter((item) => item.label);

  if (apiItems.length) return apiItems;

  const fallbackBenefits =
    String(planType || "").toUpperCase() === "PERSONAL"
      ? personalPlanBenefits(plan?.code)
      : businessPlanBenefits(plan?.code);

  return fallbackBenefits.map((label, index) => ({
    key: `${firstString(plan?.code, "plan")}-${index}`,
    label,
    description: "",
  }));
};

const addYears = (date, years) => {
  const next = new Date(date);
  next.setFullYear(next.getFullYear() + years);
  return next;
};

const addMonthsClamped = (date, months) => {
  const next = new Date(date);
  const originalDay = date.getDate();
  next.setDate(1);
  next.setMonth(next.getMonth() + months);
  const lastDay = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate();
  next.setDate(Math.min(originalDay, lastDay));
  next.setHours(date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());
  return next;
};

const subscriptionCycleInfo = (startDate, now = new Date()) => {
  const start = startDate ? new Date(startDate) : null;
  if (!start || !Number.isFinite(start.getTime())) {
    return {
      cycleEndDate: new Date(addMonthsClamped(now, 1).getTime() - 1),
      usedMonths: 1,
    };
  }

  let usedMonths = 1;
  let nextBoundary = addMonthsClamped(start, usedMonths);
  while (nextBoundary.getTime() <= now.getTime() && usedMonths < 120) {
    usedMonths += 1;
    nextBoundary = addMonthsClamped(start, usedMonths);
  }

  return {
    cycleEndDate: new Date(nextBoundary.getTime() - 1),
    usedMonths: Math.min(12, usedMonths),
  };
};

const normalizedPaymentType = (value) => {
  const nextValue = String(value || "").toUpperCase();
  if (nextValue === "MONTHLY" || nextValue === "MONTH") return "MONTHLY";
  if (["ANNUALLY", "ANNUAL", "YEARLY", "YEAR"].includes(nextValue)) return "ANNUALLY";
  return "";
};

const subscriptionPeriod = (paymentType, start = new Date()) => {
  const startDate = new Date(start);
  const endDate = paymentType === "ANNUALLY"
    ? addYears(startDate, 1)
    : addMonthsClamped(startDate, 1);

  return {
    startDate,
    endDate,
  };
};

const WorkspaceSubscriptionsPage = () => {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const actorId = user?.id || user?.userId || "";
  const [account, setAccount] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creatingPlanKey, setCreatingPlanKey] = useState("");
  const [selectedPlanCode, setSelectedPlanCode] = useState("");
  const [selectedPaymentTypes, setSelectedPaymentTypes] = useState({});
  const [chargePreview, setChargePreview] = useState(null);
  const [subscriptionBgpCode, setSubscriptionBgpCode] = useState("");
  const [subscriptionCouponCodes, setSubscriptionCouponCodes] = useState("");
  const [bgpCodeWarning, setBgpCodeWarning] = useState("");
  const [cancelPreview, setCancelPreview] = useState(null);
  const currentContext = useMemo(
    () => subscriptionContext(account, actorId),
    [account, actorId],
  );

  const loadData = useCallback(async () => {
    if (!workspaceId || !actorId) return;

    try {
      setLoading(true);
      const nextAccount = await fetchWorkspace(workspaceId, actorId);
      const nextContext = subscriptionContext(nextAccount, actorId);
      setAccount(nextAccount);

      const requests = [
        identitySubscriptionService.plans.list({
          code: "OLREADIA",
          status: "ACTIVE",
          type: nextContext.planType,
          page: 0,
          size: 100,
        }),
      ];

      if (nextContext.ready) {
        requests.push(
          identitySubscriptionService.subscriptions.list({
            subscriberType: nextContext.subscriberType,
            subscriberId: nextContext.subscriberId,
            workspaceId,
            organizationId: nextContext.organizationId || undefined,
            code: "OLREADIA",
            status: "ACTIVE",
            page: 0,
            size: 100,
            sort: "startDate,desc",
          }),
        );
      } else {
        requests.push(Promise.resolve({ content: [] }));
      }

      const [plansResult, subscriptionsResult] = await Promise.all(requests);
      const sortedPlans = sortPlans(pageContent(plansResult));
      const nextSubscriptions = pageContent(subscriptionsResult);
      const activeBusinessSubscription =
        nextSubscriptions.find((subscription) => String(subscription?.status || "").toUpperCase() === "ACTIVE") ||
        nextSubscriptions[0] ||
        null;
      const nextCurrentSubscription = activeBusinessSubscription || null;
      const fallbackPaymentType = normalizedPaymentType(
        nextCurrentSubscription?.subscriptionPaymentType || nextSubscriptions[0]?.subscriptionPaymentType,
      );
      const fallbackPlanCode = firstString(
        nextCurrentSubscription?.subscriptionPlanId,
        nextCurrentSubscription?.subscriptionPlanCode,
        nextCurrentSubscription?.plan?.code,
        nextSubscriptions[0]?.subscriptionPlanId,
        nextSubscriptions[0]?.subscriptionPlanCode,
        nextSubscriptions[0]?.plan?.code,
      );

      setPlans(sortedPlans);
      setSubscriptions(nextSubscriptions);
      setCurrentSubscription(nextCurrentSubscription);
      setSelectedPlanCode("");
      setSubscriptionBgpCode(firstString(nextCurrentSubscription?.bgpCode));
      setSubscriptionCouponCodes(couponCodesText(nextCurrentSubscription?.couponCodes || []));
      if (fallbackPlanCode && fallbackPaymentType) {
        setSelectedPaymentTypes((current) => ({
          ...current,
          [fallbackPlanCode]: fallbackPaymentType,
        }));
      }
    } catch (error) {
      message.error(error?.message || "Failed to load subscription options.");
    } finally {
      setLoading(false);
    }
  }, [actorId, workspaceId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const availableSubscriptions = useMemo(() => {
    const byId = new Map();
    subscriptions.forEach((subscription) => {
      if (subscription?.id) byId.set(subscription.id, subscription);
    });
    if (currentSubscription?.id) {
      byId.set(currentSubscription.id, currentSubscription);
    }
    return Array.from(byId.values());
  }, [currentSubscription, subscriptions]);

  const currentPlanCode = firstString(currentSubscription?.subscriptionPlanCode, currentSubscription?.plan?.code);
  const currentPlanId = firstString(currentSubscription?.subscriptionPlanId, currentSubscription?.plan?.id);
  const currentPlanName = firstString(currentSubscription?.subscriptionPlanName, currentSubscription?.plan?.name);
  const currentPlanKey = currentPlanId || currentPlanCode;
  const currentSubscriptionId = firstString(currentSubscription?.id, currentSubscription?.subscriptionId);
  const currentPaymentType = normalizedPaymentType(currentSubscription?.subscriptionPaymentType);
  const defaultFreePlan = useMemo(() => plans.find(isFreePlan) || null, [plans]);
  const effectiveCurrentPlanCode = currentPlanCode || firstString(defaultFreePlan?.code);
  const includedPlanIsActive = Boolean(!currentSubscription && defaultFreePlan);
  const currentPlan = useMemo(
    () =>
      plans.find((plan) => {
        const planCode = firstString(plan?.code);
        const planId = firstString(plan?.id);
        const name = firstString(plan?.name);
        if (currentPlanId) return planId === currentPlanId;
        if (currentPlanName) return name === currentPlanName;
        return currentPlanCode && planCode === currentPlanCode;
      }) ||
      currentSubscription?.plan ||
      defaultFreePlan ||
      null,
    [currentPlanCode, currentPlanId, currentPlanName, currentSubscription, defaultFreePlan, plans],
  );
  const selectedPlan = useMemo(
    () =>
      plans.find((plan) => {
        const key = planKey(plan);
        return (
          key === selectedPlanCode ||
          firstString(plan?.id) === selectedPlanCode ||
          firstString(plan?.code) === selectedPlanCode
        );
      }) || null,
    [plans, selectedPlanCode],
  );
  const selectedPaymentType = selectedPlanCode
    ? selectedPaymentTypes[selectedPlanCode] ||
      (selectedPlanCode === currentPlanKey && currentPaymentType ? currentPaymentType : "MONTHLY")
    : "MONTHLY";
  const selectedBgpCode = normalizeBgpCode(subscriptionBgpCode);
  const selectedCouponCodes = normalizeCouponCodes(subscriptionCouponCodes);
  const subscriberContext = useMemo(
    () => subscriptionContext(account, actorId),
    [account, actorId],
  );
  const currentBgpCode = normalizeBgpCode(currentSubscription?.bgpCode);
  const currentCouponCodes = normalizeCouponCodes(
    couponCodesText(currentSubscription?.couponCodes || currentSubscription?.paymentChargeDetails?.couponCodes || []),
  );
  const selectedCouponCodesText = couponCodesText(selectedCouponCodes);
  const currentCouponCodesText = couponCodesText(currentCouponCodes);
  const hasSaveChanges = Boolean(
    subscriberContext.ready &&
      selectedPlanCode &&
      !isFreePlanCode(selectedPlanCode) &&
      (
        selectedPlanCode !== (currentPlanKey || effectiveCurrentPlanCode) ||
        selectedPaymentType !== currentPaymentType ||
        selectedBgpCode !== currentBgpCode ||
        selectedCouponCodesText !== currentCouponCodesText
      ),
  );

  const saveOrganizationSubscription = async (subscriptionId) => {
    if (!subscriptionId) return false;

    try {
      const subscription = await identitySubscriptionService.subscriptions.get(subscriptionId);
      setCurrentSubscription(subscription || null);
      message.success("Subscription saved to the attached organization.");
      return true;
    } catch (error) {
      message.error(error?.message || "Failed to save subscription.");
      return false;
    }
  };

  const validateBgpCodeForSubmission = async (bgpCode = "") => {
    const normalizedCode = String(bgpCode || "").trim();
    if (!normalizedCode) {
      setBgpCodeWarning("");
      return true;
    }

    try {
      const result = await partnersService.list({ page: 0, size: 500 });
      const partner = pageContent(result).find(
        (item) => String(item?.referralCode || "").trim().toLowerCase() === normalizedCode.toLowerCase(),
      );

      if (!partner) {
        const warning = "This BGP code was not found. Check the code before saving the subscription.";
        setBgpCodeWarning(warning);
        message.warning(warning);
        return false;
      }

      const status = String(partner.status || "").toUpperCase();
      if (!["ACTIVE", "PENDING"].includes(status)) {
        const warning = `This BGP code is ${status || "not active"} and cannot be used for this subscription.`;
        setBgpCodeWarning(warning);
        message.warning(warning);
        return false;
      }

      setBgpCodeWarning("");
      return true;
    } catch (error) {
      const warning = error?.message || "Unable to validate the BGP code. Try again before saving.";
      setBgpCodeWarning(warning);
      message.error(warning);
      return false;
    }
  };

  const createAndSelectSubscription = async (
    plan,
    subscriptionPaymentType,
    bgpCode = "",
    period = subscriptionPeriod(subscriptionPaymentType),
    couponCodes = [],
  ) => {
    if (!subscriberContext.ready) {
      message.error(
        isPersonalAccount(account)
          ? "A user is required before creating a personal subscription."
          : "An organization is required before creating a business subscription.",
      );
      return false;
    }

    const createKey = `${plan.id}-${subscriptionPaymentType}`;
    const normalizedBgpCode = normalizeBgpCode(bgpCode);
    const normalizedCouponCodes = normalizeCouponCodes(couponCodesText(couponCodes));
    try {
      setCreatingPlanKey(createKey);
      const subscription = await identitySubscriptionService.subscriptions.create(
        {
          subscriberType: subscriberContext.subscriberType,
          subscriberId: subscriberContext.subscriberId,
          workspaceId,
          organizationId: subscriberContext.organizationId || undefined,
          subscriptionPlanId: plan.id,
          subscriptionPlanCode: plan.code,
          startDate: period.startDate.toISOString(),
          endDate: period.endDate.toISOString(),
          subscriptionPaymentType,
          bgpCode: normalizedBgpCode || undefined,
          couponCodes: normalizedCouponCodes.length ? normalizedCouponCodes : undefined,
          currency: "USD",
          status: "ACTIVE",
        },
        { workspaceId },
      );
      setSubscriptions((current) => [subscription, ...current]);
      setCurrentSubscription(subscription);
      message.success("Subscription saved to the attached organization.");
      return true;
    } catch (error) {
      message.error(error?.message || "Failed to create subscription.");
      return false;
    } finally {
      setCreatingPlanKey("");
    }
  };

  const updateAndSelectSubscription = async (
    subscriptionId,
    plan,
    subscriptionPaymentType,
    bgpCode = "",
    period = subscriptionPeriod(subscriptionPaymentType),
    couponCodes = [],
  ) => {
    if (!subscriptionId) return false;

    const updateKey = `${plan.id}-${subscriptionPaymentType}`;
    const normalizedBgpCode = normalizeBgpCode(bgpCode);
    const normalizedCouponCodes = normalizeCouponCodes(couponCodesText(couponCodes));
    try {
      setCreatingPlanKey(updateKey);
      const subscription = await identitySubscriptionService.subscriptions.update(
        subscriptionId,
        {
          subscriptionPlanId: plan.id,
          subscriptionPlanCode: plan.code,
          workspaceId,
          organizationId: subscriberContext.organizationId || undefined,
          startDate: period.startDate.toISOString(),
          endDate: period.endDate.toISOString(),
          subscriptionPaymentType,
          bgpCode: normalizedBgpCode || "",
          couponCodes: normalizedCouponCodes,
          currency: "USD",
          status: "ACTIVE",
        },
        { workspaceId },
      );
      setSubscriptions((current) => [subscription, ...current.filter((item) => item?.id !== subscription?.id)]);
      setCurrentSubscription(subscription);
      message.success("Subscription saved to the attached organization.");
      return true;
    } catch (error) {
      message.error(error?.message || "Failed to update subscription.");
      return false;
    } finally {
      setCreatingPlanKey("");
    }
  };

  const findMatchingSubscription = (plan, subscriptionPaymentType, ignoredSubscriptionId = "") => {
    const planCode = firstString(plan?.code);
    const planId = firstString(plan?.id);

    return availableSubscriptions.find((subscription) => {
      if (ignoredSubscriptionId && firstString(subscription?.id, subscription?.subscriptionId) === ignoredSubscriptionId) {
        return false;
      }
      const subscriptionPlanCode = firstString(subscription?.subscriptionPlanCode, subscription?.plan?.code);
      const subscriptionPlanId = firstString(subscription?.subscriptionPlanId, subscription?.plan?.id);
      const samePlan =
        (planCode && subscriptionPlanCode === planCode) ||
        (planId && subscriptionPlanId === planId);
      return samePlan && normalizedPaymentType(subscription?.subscriptionPaymentType) === subscriptionPaymentType;
    });
  };

  const persistSelectedSubscription = async (
    ignoredSubscriptionId = "",
    bgpCode = "",
    period = subscriptionPeriod(selectedPaymentType),
    couponCodes = selectedCouponCodes,
  ) => {
    if (!selectedPlan) return;
    if (isFreePlan(selectedPlan)) return;

    setCreatingPlanKey(selectedPlanCode);
    const normalizedBgpCode = normalizeBgpCode(bgpCode);
    const normalizedCouponCodes = normalizeCouponCodes(couponCodesText(couponCodes));
    const matchingSubscription = findMatchingSubscription(selectedPlan, selectedPaymentType, ignoredSubscriptionId);
    try {
      if (currentSubscriptionId && !ignoredSubscriptionId) {
        return await updateAndSelectSubscription(
          currentSubscriptionId,
          selectedPlan,
          selectedPaymentType,
          bgpCode,
          period,
          normalizedCouponCodes,
        );
      }

      if (
        matchingSubscription?.id &&
        normalizeBgpCode(matchingSubscription?.bgpCode) === normalizedBgpCode &&
        couponCodesText(normalizeCouponCodes(couponCodesText(matchingSubscription?.couponCodes || []))) === couponCodesText(normalizedCouponCodes)
      ) {
        return await saveOrganizationSubscription(matchingSubscription.id);
      }

      return await createAndSelectSubscription(selectedPlan, selectedPaymentType, bgpCode, period, normalizedCouponCodes);
    } finally {
      setCreatingPlanKey("");
    }
  };

  const buildPlanChangeRefundPreview = useCallback(() => {
    if (!currentSubscriptionId || !currentSubscription || currentPaymentType !== "ANNUALLY") return null;
    if (!selectedPlanCode) return null;
    if (selectedPlanCode === (currentPlanKey || effectiveCurrentPlanCode) && selectedPaymentType === currentPaymentType) return null;

    const { cycleEndDate, usedMonths } = subscriptionCycleInfo(currentSubscription.startDate);
    const currentPaidAmount = moneyNumber(currentSubscription?.amount);
    const annualAmount = moneyNumber(currentPlan?.annualAmount) || currentPaidAmount;
    const monthlyRate = moneyNumber(currentPlan?.monthlyAmount) || (annualAmount > 0 ? annualAmount / 12 : 0);
    const refundEstimate = annualAmount > 0 && monthlyRate > 0
      ? annualAmount - (monthlyRate * usedMonths)
      : 0;
    const remainingCreditAmount = refundEstimate > 0 ? money(refundEstimate) : "";
    if (!remainingCreditAmount) return null;

    const currentPlanName =
      currentSubscription.subscriptionPlanName ||
      currentPlan?.name ||
      planLabel(currentPlan, readable(currentPlanCode, "current plan"));

    return {
      refundSubscriptionId: currentSubscriptionId,
      remainingCreditValue: refundEstimate,
      remainingCreditAmount,
      remainingCreditSummary: `${remainingCreditAmount} remaining value from your current annual ${currentPlanName} plan after keeping ${usedMonths} month${usedMonths === 1 ? "" : "s"} at the monthly rate.`,
      refundDetail: "We will apply the remaining value to the new subscription after you confirm.",
      currentPlanName,
      currentPaymentType,
      currentExpiryDate: currentSubscription.endDate || "",
      refundCycleEndDate: cycleEndDate.toISOString(),
    };
  }, [
    currentPaymentType,
    currentPlan,
    currentPlanCode,
    currentPlanKey,
    currentSubscription,
    currentSubscriptionId,
    effectiveCurrentPlanCode,
    selectedPaymentType,
    selectedPlanCode,
  ]);

  const saveSelectedSubscription = async () => {
    if (!selectedPlan) return;
    if (isFreePlan(selectedPlan)) return;
    const bgpCodeIsValid = await validateBgpCodeForSubmission(subscriptionBgpCode);
    if (!bgpCodeIsValid) return;

    const planCode = firstString(selectedPlan?.code, selectedPlanCode);
    const period = subscriptionPeriod(selectedPaymentType);
    const couponCodes = normalizeCouponCodes(subscriptionCouponCodes);
    const planChargeAmount =
      selectedPaymentType === "ANNUALLY"
        ? moneyNumber(selectedPlan?.annualAmount)
        : moneyNumber(selectedPlan?.monthlyAmount);
    const planChangePreview = buildPlanChangeRefundPreview();
    const remainingCredit = moneyNumber(planChangePreview?.remainingCreditValue);
    const netBalance = planChangePreview ? remainingCredit - planChargeAmount : 0;
    const displayNetRefund = netBalance > 0 ? money(netBalance) : "";
    const displayNetCharge = planChangePreview && netBalance < 0 ? money(Math.abs(netBalance)) : "";
    const balanceDetail = displayNetCharge
      ? `${planChangePreview.remainingCreditAmount} remaining value will be applied. Additional balance due: ${displayNetCharge}.`
      : "";
    const monthlyPlanChangeDetail =
      currentSubscriptionId && currentPaymentType === "MONTHLY" && selectedPlanCode !== (currentPlanKey || effectiveCurrentPlanCode)
        ? "Your current monthly subscription remains usable through the end of the current month. No refund is calculated for monthly subscriptions."
        : "";
    const planChangeDetail = planChangePreview
      ? [
          planChangePreview.remainingCreditSummary,
          balanceDetail ||
            (displayNetRefund
              ? `Net refundable amount after the new plan charge: ${displayNetRefund}.`
              : "The remaining value will be applied to this subscription change."),
        ].filter(Boolean).join(" ")
      : "";

    setChargePreview({
      planCode,
      planName: planLabel(selectedPlan),
      paymentType: selectedPaymentType,
      planChargeAmount: money(planChargeAmount),
      chargeAmount: displayNetRefund || displayNetCharge || money(planChargeAmount),
      refundAmount: displayNetRefund,
      netRefundAmount: displayNetRefund,
      netChargeAmount: displayNetCharge,
      annualSavings: planAnnualSavings(selectedPlan),
      bgpCode: selectedBgpCode || undefined,
      couponCodes,
      startDate: period.startDate.toISOString(),
      endDate: period.endDate.toISOString(),
      detail:
        planChangeDetail ||
        monthlyPlanChangeDetail ||
        (selectedPaymentType === "MONTHLY"
          ? "Monthly billing charges one month now."
          : "Annual billing charges the full annual amount now."),
      refundSummary: displayNetRefund
        ? `Estimated net refund after applying ${planChangePreview.remainingCreditAmount} remaining value: ${displayNetRefund}.`
        : "",
      ...(planChangePreview || {}),
    });
  };

  const confirmChargePreview = async () => {
    if (!chargePreview || creatingPlanKey) return;

    try {
      const bgpCodeIsValid = await validateBgpCodeForSubmission(subscriptionBgpCode);
      if (!bgpCodeIsValid) return;
      setCreatingPlanKey(chargePreview.planCode || selectedPlanCode);
      const period = {
        startDate: new Date(chargePreview.startDate),
        endDate: new Date(chargePreview.endDate),
      };
      const couponCodes = normalizeCouponCodes(subscriptionCouponCodes).length
        ? normalizeCouponCodes(subscriptionCouponCodes)
        : chargePreview.couponCodes || [];
      const saved = await persistSelectedSubscription(
        "",
        normalizeBgpCode(subscriptionBgpCode) || chargePreview.bgpCode || "",
        period,
        couponCodes,
      );
      if (saved) {
        setChargePreview(null);
        setSubscriptionBgpCode("");
        setSubscriptionCouponCodes("");
        await loadData();
      }
    } catch (error) {
      message.error(error?.message || "Unable to update subscription.");
    } finally {
      setCreatingPlanKey("");
    }
  };

  const buildCancelPreview = useCallback(() => {
    if (!currentSubscriptionId || !currentSubscription) return null;

    const paymentType = currentPaymentType === "ANNUALLY" ? "ANNUALLY" : "MONTHLY";
    const { cycleEndDate } = subscriptionCycleInfo(currentSubscription.startDate);
    const planName =
      currentSubscription.subscriptionPlanName ||
      currentPlan?.name ||
      planLabel(currentPlan, readable(currentPlanCode, "current plan"));
    const monthlyRate = moneyNumber(currentPlan?.monthlyAmount);
    const annualAmount = moneyNumber(currentPlan?.annualAmount);
    const refundEstimate = paymentType === "ANNUALLY" ? annualAmount : monthlyRate;
    const refundAmount = refundEstimate > 0 ? money(refundEstimate) : "";

    return {
      subscriptionId: currentSubscriptionId,
      planName,
      paymentType,
      currentExpiryDate: currentSubscription.endDate || "",
      newExpiryDate: cycleEndDate.toISOString(),
      refundAmount,
      refundSummary: refundAmount
        ? `${refundAmount} estimated refund for the current ${paymentType === "ANNUALLY" ? "annual" : "monthly"} subscription.`
        : `No refundable ${paymentType === "ANNUALLY" ? "annual" : "monthly"} amount was found.`,
      detail:
        "Your subscription will continue working through the end of the current subscription month. We will submit the current paid subscription amount for refund handling after you confirm.",
    };
  }, [currentPaymentType, currentPlan, currentPlanCode, currentSubscription, currentSubscriptionId]);

  const handleCurrentSubscriptionLifecycle = () => {
    if (!currentSubscriptionId || creatingPlanKey) return;
    const preview = buildCancelPreview();
    if (preview) setCancelPreview(preview);
  };

  const confirmCancelPreview = async () => {
    if (!cancelPreview || creatingPlanKey) return;

    try {
      setCreatingPlanKey(currentPlanCode || cancelPreview.subscriptionId);
      await identitySubscriptionService.subscriptions.cancel(cancelPreview.subscriptionId);
      setCancelPreview(null);
      message.success("Subscription cancelled.");
      await loadData();
    } catch (error) {
      message.error(error?.message || "Unable to cancel subscription.");
    } finally {
      setCreatingPlanKey("");
    }
  };

  const renderPlan = (plan) => {
    const planCode = firstString(plan.code);
    const planId = firstString(plan.id);
    const key = planKey(plan);
    const freePlan = isFreePlan(plan);
    const selected = Boolean(!freePlan && key && key === selectedPlanCode);
    const isCurrent = Boolean(
      (currentSubscription &&
        (currentPlanId
          ? planId === currentPlanId
          : currentPlanName
            ? firstString(plan?.name) === currentPlanName
            : planCode && planCode === currentPlanCode)) ||
        (includedPlanIsActive && freePlan && planCode === effectiveCurrentPlanCode),
    );
    const planPaymentType =
      selectedPaymentTypes[key] ||
      (key === currentPlanKey && currentPaymentType ? currentPaymentType : "MONTHLY");
    const annualSavings = planAnnualSavings(plan);
    const serviceItems = planServiceItems(plan, currentContext.planType);
    const billingButton = (label, value) => {
      const currentBilling = isCurrent && value === currentPaymentType;
      const selectedBilling = selected && planPaymentType === value && !currentBilling;

      return (
        <Button
          block
          key={value}
          onClick={(event) => {
            event.stopPropagation();
            setSelectedPlanCode(key);
            setSelectedPaymentTypes((current) => ({
              ...current,
              [key]: value,
            }));
          }}
          style={{
            borderColor: currentBilling
              ? OLIVEEB_COLORS.oliveebGreen
              : selectedBilling
                ? OLIVEEB_COLORS.oliveebOrange
                : OLIVEEB_COLORS.border,
            background: currentBilling
              ? OLIVEEB_COLORS.oliveebGreen
              : selectedBilling
                ? OLIVEEB_COLORS.oliveebOrange
                : OLIVEEB_COLORS.white,
            color: currentBilling || selectedBilling ? OLIVEEB_COLORS.white : OLIVEEB_COLORS.textPrimary,
            fontWeight: currentBilling || selectedBilling ? 800 : 600,
          }}
        >
          {label}
        </Button>
      );
    };

    return (
      <List.Item>
        <Card
          size="small"
          hoverable={!freePlan}
          onClick={() => {
            if (!freePlan) setSelectedPlanCode(key);
          }}
          style={{
            height: "100%",
            borderRadius: 8,
            overflow: "hidden",
            borderColor: isCurrent
              ? OLIVEEB_COLORS.oliveebGreen
              : selected
                ? OLIVEEB_COLORS.oliveebOrange
                : OLIVEEB_COLORS.oliveebBlueSoft,
            borderWidth: selected || isCurrent ? 2 : 1,
            background: isCurrent
              ? OLIVEEB_COLORS.greenSoft2
              : selected
                ? "#FFF3E8"
                : OLIVEEB_COLORS.card,
          }}
          bodyStyle={{ position: "relative", height: "100%" }}
        >
          <div
            style={{
              position: "absolute",
              inset: "0 0 auto",
              height: 4,
              background: isCurrent
                ? OLIVEEB_COLORS.oliveebGreen
                : selected
                  ? OLIVEEB_COLORS.oliveebOrange
                  : OLIVEEB_COLORS.goldBorder,
            }}
          />
          <Space direction="vertical" size={10} style={{ width: "100%" }}>
            <div className="d-flex justify-content-between align-items-start gap-2">
              <div style={{ minWidth: 0 }}>
                <Text strong style={{ display: "block", wordBreak: "break-word" }}>
                  {planLabel(plan)}
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {plan.description || readable(plan.subscriptionType, "Subscription type")}
                </Text>
              </div>
              {freePlan ? (
                <Tag
                  color={isCurrent ? "green" : "default"}
                  style={{ fontWeight: 800, fontSize: 13 }}
                >
                  Included
                </Tag>
              ) : isCurrent ? (
                <Tag color="green" style={{ fontWeight: 800, fontSize: 13 }}>
                  Current
                </Tag>
              ) : selected ? (
                <Tag color="gold" style={{ fontWeight: 800, fontSize: 13 }}>
                  Selected
                </Tag>
              ) : null}
            </div>

            {serviceItems.length ? (
              <div
                style={{
                  display: "grid",
                  gap: 8,
                  border: `1px solid ${OLIVEEB_COLORS.oliveebBlueSoft}`,
                  borderRadius: 8,
                  padding: "10px 12px",
                  background: OLIVEEB_COLORS.white,
                }}
              >
                <Text strong style={{ color: OLIVEEB_COLORS.oliveebDarkBlue, fontSize: 13 }}>
                  Included
                </Text>
                <Space direction="vertical" size={7} style={{ width: "100%" }}>
                  {serviceItems.map((item) => (
                    <div
                      key={item.key}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "18px minmax(0, 1fr)",
                        gap: 8,
                        alignItems: "start",
                      }}
                    >
                      <CheckCircleOutlined
                        style={{
                          color: OLIVEEB_COLORS.oliveebGreen,
                          fontSize: 14,
                          marginTop: 3,
                        }}
                      />
                      <span style={{ minWidth: 0 }}>
                        <Text style={{ display: "block", color: OLIVEEB_COLORS.textPrimary, fontSize: 13 }}>
                          {item.label}
                        </Text>
                        {item.description ? (
                          <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                            {item.description}
                          </Text>
                        ) : null}
                      </span>
                    </div>
                  ))}
                </Space>
              </div>
            ) : null}

            {freePlan ? (
              <div
                style={{
                  border: `1px solid ${isCurrent ? OLIVEEB_COLORS.oliveebGreen : OLIVEEB_COLORS.border}`,
                  borderRadius: 8,
                  padding: "10px 12px",
                  background: isCurrent ? OLIVEEB_COLORS.greenSoft2 : OLIVEEB_COLORS.bgSoft,
                  color: OLIVEEB_COLORS.oliveebDarkGreen,
                  fontWeight: isCurrent ? 700 : 500,
                }}
              >
                Included when no paid subscription is active.
              </div>
            ) : (
              <>
                <Space.Compact block>
                  {billingButton(`Monthly ${planMonthlyPrice(plan)}`, "MONTHLY")}
                  {billingButton(`Annual ${planAnnualPrice(plan)}`, "ANNUALLY")}
                </Space.Compact>
                {planPaymentType === "MONTHLY" ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Annual total if paid monthly: {planAnnualizedMonthlyPrice(plan)}
                  </Text>
                ) : null}
                {annualSavings ? (
                  <Text style={{ color: OLIVEEB_COLORS.oliveebGreen, fontSize: 12 }}>
                    {planPaymentType === "MONTHLY"
                      ? `Save ${annualSavings} by choosing annual billing`
                      : `You save ${annualSavings} with annual billing`}
                  </Text>
                ) : null}
                {isCurrent ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Active {readable(currentPaymentType, "subscription")} through{" "}
                    {formatDate(currentSubscription?.endDate) || "-"}
                  </Text>
                ) : null}
                {isCurrent && currentSubscription?.bgpCode ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    BGP Code: <Text code>{currentSubscription.bgpCode}</Text>
                  </Text>
                ) : null}
                {isCurrent && currentCouponCodes.length ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Coupons: <Text code>{couponCodesText(currentCouponCodes)}</Text>
                  </Text>
                ) : null}
                {selected ? (
                  <Tag
                    color={hasSaveChanges && selected ? "gold" : "default"}
                    style={{ fontWeight: 800, fontSize: 13 }}
                  >
                    {isCurrent ? "Current selection" : "Ready to save"}
                  </Tag>
                ) : null}
                {selected ? (
                  <div
                    onClick={(event) => event.stopPropagation()}
                    style={{
                      border: `1px solid ${OLIVEEB_COLORS.oliveebGreenSoft}`,
                      borderRadius: 8,
                      padding: 10,
                      background: OLIVEEB_COLORS.white,
                    }}
                  >
                    <Text strong style={{ color: OLIVEEB_COLORS.oliveebDarkBlue, fontSize: 13 }}>
                      BGP Code (Referral Code)
                    </Text>
                    <Input
                      value={subscriptionBgpCode}
                      onChange={(event) => {
                        setSubscriptionBgpCode(event.target.value);
                        setBgpCodeWarning("");
                      }}
                      placeholder="Optional Business Growth Partner code"
                      maxLength={80}
                      autoComplete="off"
                      style={{
                        marginTop: 5,
                        borderColor: OLIVEEB_COLORS.oliveebGreen,
                        borderRadius: 14,
                        boxShadow: `0 10px 22px ${OLIVEEB_COLORS.oliveebGreen}22`,
                      }}
                    />
                    {bgpCodeWarning ? (
                      <Text type="danger" style={{ display: "block", marginTop: 6, fontSize: 12 }}>
                        {bgpCodeWarning}
                      </Text>
                    ) : null}
                    <Text strong style={{ color: OLIVEEB_COLORS.oliveebDarkBlue, display: "block", fontSize: 13, marginTop: 12 }}>
                      Coupon Codes
                    </Text>
                    <Input
                      value={subscriptionCouponCodes}
                      onChange={(event) => setSubscriptionCouponCodes(event.target.value.toUpperCase())}
                      placeholder="Optional codes, comma separated"
                      maxLength={96}
                      autoComplete="off"
                      style={{
                        marginTop: 5,
                        borderColor: OLIVEEB_COLORS.oliveebBlue,
                        borderRadius: 14,
                        boxShadow: `0 10px 22px ${OLIVEEB_COLORS.oliveebBlue}1f`,
                      }}
                    />
                    {selectedCouponCodes.length ? (
                      <Space wrap size={[4, 4]} style={{ marginTop: 8 }}>
                        {selectedCouponCodes.map((code) => (
                          <Tag key={code} color="purple" style={{ marginInlineEnd: 0, fontWeight: 700 }}>
                            {code}
                          </Tag>
                        ))}
                      </Space>
                    ) : null}
                  </div>
                ) : null}
              </>
            )}
          </Space>
        </Card>
      </List.Item>
    );
  };

  return (
    <div className="project-page">
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3 flex-wrap">
        <WidgetSectionTitle
          icon="credit-card"
          title="Workspace Subscriptions"
          subtitle="Select the subscription this workspace should use"
          className="flex-1"
          titleStyle={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}
          subtitleStyle={{ color: OLIVEEB_COLORS.oliveebGreen }}
        />
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            style={workspaceButtonStyles.back}
            onClick={() => navigate(paths.workspaceDetails(workspaceId))}
          >
            Back
          </Button>
          {account && (isPersonalAccount(account) || account.organizationId) ? (
            <Button
              icon={<WalletOutlined />}
              style={workspaceButtonStyles.paymentMethods}
              onClick={() => navigate(paths.workspacePaymentMethods(workspaceId))}
            >
              Payment methods
            </Button>
          ) : null}
          <Button
            icon={<ReloadOutlined />}
            style={workspaceButtonStyles.refresh}
            onClick={loadData}
          >
            Refresh
          </Button>
        </Space>
      </div>

      {loading && !account ? (
        <Card>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      ) : !account ? (
        <Card>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Workspace not found" />
        </Card>
      ) : (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          {!isPersonalAccount(account) && !account.organizationId ? (
            <Alert
              type="warning"
              showIcon
              message={
                <Text style={{ color: OLIVEEB_COLORS.oliveebBlue, fontWeight: 800 }}>
                  This workspace is not linked to an organization.
                </Text>
              }
              description={
                <Space direction="vertical" size={8} align="start">
                  <Text style={{ color: OLIVEEB_COLORS.oliveebBlue }}>
                    Business subscriptions can only be created after an organization is linked to the workspace.
                  </Text>
                  <Button
                    size="small"
                    type="primary"
                    icon={<LinkOutlined />}
                    onClick={() => navigate(paths.workspaceOrganization(workspaceId))}
                    style={{
                      background: OLIVEEB_COLORS.oliveebBlue,
                      borderColor: OLIVEEB_COLORS.oliveebBlue,
                      color: OLIVEEB_COLORS.white,
                      fontWeight: 800,
                    }}
                  >
                    Link organization
                  </Button>
                </Space>
              }
              style={{ textAlign: "left" }}
            />
          ) : null}

          <Card
            size="small"
            style={{
              borderRadius: 8,
              borderColor: OLIVEEB_COLORS.oliveebGreenSoft,
              background: OLIVEEB_COLORS.greenSoft2,
            }}
            bodyStyle={{ padding: "10px 12px" }}
          >
            <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
              <Space size={8} wrap>
                <CreditCardOutlined style={{ color: OLIVEEB_COLORS.oliveebGreen }} />
                <Text strong style={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}>
                  Current subscription
                </Text>
                <Text>
                  {currentSubscription
                    ? subscriptionLabel(currentSubscription)
                    : planLabel(defaultFreePlan, "Included subscription")}
                </Text>
              </Space>
              <Space size={6} wrap>
                {currentSubscription ? (
                  <>
                    <Tag color="purple">
                      {readable(currentSubscription.subscriptionPaymentType, "Monthly")}
                    </Tag>
                    <Tag color="green">{readable(currentSubscription.status, "Active")}</Tag>
                    {currentSubscription.bgpCode ? (
                      <Tag color="cyan" style={{ fontWeight: 700 }}>
                        BGP Code: {currentSubscription.bgpCode}
                      </Tag>
                    ) : null}
                    {currentCouponCodes.map((code) => (
                      <Tag key={code} color="purple" style={{ fontWeight: 700 }}>
                        Coupon: {code}
                      </Tag>
                    ))}
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Started {formatDate(currentSubscription.startDate) || "-"}
                    </Text>
                    {currentSubscriptionId ? (
                      <Button
                        size="small"
                        danger
                        loading={Boolean(creatingPlanKey)}
                        onClick={handleCurrentSubscriptionLifecycle}
                      >
                        Cancel subscription
                      </Button>
                    ) : null}
                  </>
                ) : (
                  <Tag color="green" style={{ fontWeight: 800 }}>
                    Included
                  </Tag>
                )}
              </Space>
            </div>
          </Card>

          <Card
            title={sectionTitle("Subscription Options")}
            extra={<Tag color="green">{plans.length} options</Tag>}
            style={{ borderRadius: 8 }}
          >
            <div
              className="d-flex justify-content-between align-items-center gap-3 flex-wrap"
              style={{
                border: `1px solid ${OLIVEEB_COLORS.border}`,
                borderRadius: 8,
                padding: 12,
                marginBottom: 16,
                background: OLIVEEB_COLORS.bgSoft,
              }}
            >
              <div style={{ minWidth: 0, flex: "1 1 320px" }}>
                <Text strong style={{ display: "block", color: OLIVEEB_COLORS.oliveebDarkGreen }}>
                  Subscription selection
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {selectedPlan
                    ? `Selected: ${planLabel(selectedPlan)}${
                        isFreePlan(selectedPlan) ? "" : ` (${readable(selectedPaymentType, "Monthly")})`
                      }`
                    : "Choose a paid subscription option to save it for the attached organization."}
                </Text>
                {selectedCouponCodes.length ? (
                  <Space wrap size={[4, 4]} style={{ marginTop: 6 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Coupons:</Text>
                    {selectedCouponCodes.map((code) => (
                      <Tag key={code} color="purple" style={{ marginInlineEnd: 0, fontWeight: 700 }}>
                        {code}
                      </Tag>
                    ))}
                  </Space>
                ) : null}
              </div>
              <Button
                type="primary"
                icon={<CreditCardOutlined />}
                disabled={!hasSaveChanges}
                loading={Boolean(creatingPlanKey)}
                onClick={saveSelectedSubscription}
              >
                Save Subscription
              </Button>
            </div>
            {plans.length ? (
              <>
                <List
                  grid={{ gutter: 12, xs: 1, md: 2, xl: 3 }}
                  dataSource={plans}
                  renderItem={renderPlan}
                />
                <div className="d-flex justify-content-end mt-2">
                  <Button
                    type="primary"
                    icon={<CreditCardOutlined />}
                    disabled={!hasSaveChanges}
                    loading={Boolean(creatingPlanKey)}
                    onClick={saveSelectedSubscription}
                  >
                    Save Subscription
                  </Button>
                </div>
              </>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No active subscription options found"
              />
            )}
          </Card>

        </Space>
      )}

      <Modal
        open={Boolean(chargePreview)}
        title="Confirm subscription update"
        okText="Confirm and save"
        cancelText="Back"
        confirmLoading={Boolean(creatingPlanKey)}
        onOk={confirmChargePreview}
        onCancel={() => {
          setChargePreview(null);
        }}
      >
        {chargePreview ? (
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            <Alert
              type={chargePreview.remainingCreditAmount ? "info" : "success"}
              showIcon
              message={
                chargePreview.netRefundAmount
                  ? `Estimated net refund: ${chargePreview.netRefundAmount}`
                  : chargePreview.netChargeAmount
                    ? `Estimated net charge: ${chargePreview.netChargeAmount}`
                    : `Estimated charge: ${chargePreview.chargeAmount}`
              }
              description={chargePreview.refundSummary || chargePreview.detail}
            />
            <div
              style={{
                border: `1px solid ${OLIVEEB_COLORS.border}`,
                borderRadius: 8,
                padding: 12,
                background: OLIVEEB_COLORS.bgSoft,
              }}
            >
              <Space direction="vertical" size={6} style={{ width: "100%" }}>
                <div className="d-flex justify-content-between gap-3">
                  <Text type="secondary">New plan</Text>
                  <Text strong>{chargePreview.planName}</Text>
                </div>
                <div className="d-flex justify-content-between gap-3">
                  <Text type="secondary">Billing</Text>
                  <Text>{readable(chargePreview.paymentType, "Monthly")}</Text>
                </div>
                <div className="d-flex justify-content-between gap-3">
                  <Text type="secondary">Plan charge</Text>
                  <Text>{chargePreview.planChargeAmount}</Text>
                </div>
                <div className="d-flex justify-content-between gap-3">
                  <Text type="secondary">Starts</Text>
                  <Text>{formatDate(chargePreview.startDate) || "-"}</Text>
                </div>
                <div className="d-flex justify-content-between gap-3">
                  <Text type="secondary">Ends</Text>
                  <Text>{formatDate(chargePreview.endDate) || "-"}</Text>
                </div>
                {selectedBgpCode ? (
                  <div className="d-flex justify-content-between gap-3">
                    <Text type="secondary">BGP Code</Text>
                    <Text code>{selectedBgpCode}</Text>
                  </div>
                ) : null}
                <div>
                  <Text type="secondary" style={{ display: "block", marginBottom: 5 }}>
                    Coupon codes
                  </Text>
                  <Input
                    value={subscriptionCouponCodes}
                    onChange={(event) => setSubscriptionCouponCodes(event.target.value.toUpperCase())}
                    placeholder="Optional codes, comma separated"
                    maxLength={96}
                    autoComplete="off"
                  />
                </div>
                {(normalizeCouponCodes(subscriptionCouponCodes).length || chargePreview.couponCodes?.length) ? (
                  <div className="d-flex justify-content-between gap-3">
                    <Text type="secondary">Coupons</Text>
                    <Text code>
                      {couponCodesText(
                        normalizeCouponCodes(subscriptionCouponCodes).length
                          ? normalizeCouponCodes(subscriptionCouponCodes)
                          : chargePreview.couponCodes,
                      )}
                    </Text>
                  </div>
                ) : null}
                {chargePreview.remainingCreditAmount ? (
                  <div className="d-flex justify-content-between gap-3">
                    <Text type="secondary">Refundable current value</Text>
                    <Text style={{ color: OLIVEEB_COLORS.oliveebGreen }}>
                      {chargePreview.remainingCreditAmount}
                    </Text>
                  </div>
                ) : null}
                {chargePreview.remainingCreditSummary ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {chargePreview.remainingCreditSummary}
                  </Text>
                ) : null}
                {chargePreview.netChargeAmount ? (
                  <div className="d-flex justify-content-between gap-3">
                    <Text type="secondary">Balance to add</Text>
                    <Text style={{ color: OLIVEEB_COLORS.oliveebOrange }}>
                      {chargePreview.netChargeAmount}
                    </Text>
                  </div>
                ) : null}
                {chargePreview.refundAmount ? (
                  <>
                    <div className="d-flex justify-content-between gap-3">
                      <Text type="secondary">Refund estimate</Text>
                      <Text style={{ color: OLIVEEB_COLORS.oliveebGreen }}>
                        {chargePreview.refundAmount}
                      </Text>
                    </div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {chargePreview.refundDetail}
                    </Text>
                  </>
                ) : null}
                {chargePreview.annualSavings ? (
                  <Text style={{ color: OLIVEEB_COLORS.oliveebGreen, fontSize: 12 }}>
                    Annual savings: {chargePreview.annualSavings}
                  </Text>
                ) : null}
              </Space>
            </div>
          </Space>
        ) : null}
      </Modal>

      <Modal
        open={Boolean(cancelPreview)}
        title="Cancel subscription"
        okText="Confirm cancellation"
        okButtonProps={{ danger: true }}
        cancelText="Back"
        confirmLoading={Boolean(creatingPlanKey)}
        onOk={confirmCancelPreview}
        onCancel={() => setCancelPreview(null)}
      >
        {cancelPreview ? (
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            <Alert
              type="warning"
              showIcon
              message={cancelPreview.refundSummary}
              description={cancelPreview.detail}
            />
            <div
              style={{
                border: `1px solid ${OLIVEEB_COLORS.border}`,
                borderRadius: 8,
                padding: 12,
                background: OLIVEEB_COLORS.bgSoft,
              }}
            >
              <Space direction="vertical" size={6} style={{ width: "100%" }}>
                <div className="d-flex justify-content-between gap-3">
                  <Text type="secondary">Current plan</Text>
                  <Text strong>{cancelPreview.planName}</Text>
                </div>
                <div className="d-flex justify-content-between gap-3">
                  <Text type="secondary">Billing</Text>
                  <Text>{readable(cancelPreview.paymentType, "Monthly")}</Text>
                </div>
                <div className="d-flex justify-content-between gap-3">
                  <Text type="secondary">Current expiry</Text>
                  <Text>{formatDate(cancelPreview.currentExpiryDate) || "-"}</Text>
                </div>
                <div className="d-flex justify-content-between gap-3">
                  <Text type="secondary">Continues until</Text>
                  <Text>{formatDate(cancelPreview.newExpiryDate) || "-"}</Text>
                </div>
                <div className="d-flex justify-content-between gap-3">
                  <Text type="secondary">Refund estimate</Text>
                  <Text style={{ color: OLIVEEB_COLORS.oliveebGreen }}>
                    {cancelPreview.refundAmount || "-"}
                  </Text>
                </div>
              </Space>
            </div>
          </Space>
        ) : null}
      </Modal>
    </div>
  );
};

export default WorkspaceSubscriptionsPage;
